// This is from the member.teladoc.com util to help build the websdkconfig
import {MEDOPT_SIGNUP} from '@teladoc/fe-ccm/ui/config';
import {
    extractUtmParams,
    persistUtmCookie as persistSharedUtmCookie,
} from '@teladoc/fe-ccm/ui/common/utilities/url-utils';
import OneAppUserAPI from '@/user/user-oneapp-api';
import {
    DEFAULT_BRAND,
    SMB_HY_BRAND,
    PREPROD_NODE_NAME,
    PM_UNMASKED_NAME,
} from '@/config';

export const getUrlParameter = name => {
    const nameEscaped = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${nameEscaped}=([^&#]*)`);
    const results = regex.exec(window.location.search);

    return results === null
        ? null
        : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const getVideoRoomExitPath = () => getUrlParameter('exitPath');

// TODO: This should probably be integrated into getCurrentUrlWithAddedParams
// so they are handled consistently. This would be the generalized case and
// getCurrentUrlWithAddedParams would be the specific case where your base
// URL is the current window location
export const addParamToUrl = (url, param) => {
    if (url.includes('?')) {
        return `${url}&${param}`;
    }

    return `${url}?${param}`;
};

/**
 * Builds a query string containing only UTM marketing parameters from the current window location search or string provided as an argument.
 *
 * Example usage: Enables ability to append UTM params to other urls, keeping attribution intact as users are redirected to other services and pages.
 * @param {string} [search=window.location.search] Raw search string (including leading '?').
 * @returns {string} A query string prefixed with '?' if any utm_* params exist, otherwise ''.
 */
export const buildUtmQuery = (search = window.location.search) => {
    const utmParams = extractUtmParams(search);

    if (!Object.keys(utmParams).length) {
        return '';
    }

    // eslint-disable-next-line node/prefer-global/url-search-params
    const queryString = new URLSearchParams(utmParams).toString();

    return `?${queryString}`;
};

export const persistUtmCookie = persistSharedUtmCookie;

export const getCurrentUrlWithAddedParams = newParams => {
    const pathname = window.location.pathname;
    const query = window.location.search;

    if (Object.keys(newParams).length === 0) {
        return pathname + query;
    }

    const newParamsQuery = Object.entries(newParams)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
    const newQuery = query
        ? `${query}&${newParamsQuery}`
        : `?${newParamsQuery}`;

    return pathname + newQuery;
};

export const prependProtocol = link => {
    const regex = /^https?:\/\/|^\/\//i;

    if (!regex.test(link)) {
        return `https://${link}`;
    }

    return link;
};

/* Use for URLs with the following formats. !!Note that PREFIXES with hyphens will cause this to break!!

AWS: PREFIX.NAMESPACE.CLUSTER.DOMAIN
e.g. my.devsd11572.qa.teladoc.io

Azure:
PREFIX-NAMESPACE.VISIBILITY.CLUSTER.DOMAIN
e.g. my-devsd11572.private.qa.tdoctest.com
*/
export const getCurrentUrlPrefix = () => {
    return window.location.host.split(/[.-]/, 1)[0];
};

/* returns NAMESPACE.CLUSTER.DOMAIN from -
AWS: PREFIX.NAMESPACE.CLUSTER.DOMAIN
Azure: PREFIX-NAMESPACE.VISIBILITY.CLUSTER.DOMAIN

Note that this function returns a value with either a period or hyphen as the first character,
depending on which cloud provider this is hosted on. The character is left on for ease of
combining back into a full host URL, as seen in replacePrefixWith() below. If you want to
take this and separate it out into the remaining nodes, please see getNodesAfterPrefix() below
*/
export const getCurrentUrlHostWithoutPrefix = () => {
    const prefixLength = getCurrentUrlPrefix().length;

    return window.location.host.substring(prefixLength);
};

/* returns individual nodes (aside from prefix) from URL as an array:
AWS: [NAMESPACE, CLUSTER, DOMAIN]
Azure: [NAMESPACE, VISIBILITY, CLUSTER, DOMAIN]

While this is currently not being used, it is provided here to show that aside from the
separation between prefix and namespace, the other node boundaries should still be
separated by periods. The (1) is needed because getCurrentUrlHostWihoutPrefix above
returns a value with a period or hyphen on the front
*/
export const getNodesAfterPrefix = () => {
    return getCurrentUrlHostWithoutPrefix().substring(1).split('.');
};

export const replaceCurrentPrefixWith = updatedPrefix => {
    return `https://${updatedPrefix}${getCurrentUrlHostWithoutPrefix()}`;
};

export const pliProdMemberHost = () => {
    const prefixLength = getCurrentUrlPrefix().length + 1;

    return `https://${window.location.host.substring(prefixLength)}`;
};

export const getUpdatedWebsdkURL = country => {
    let baseUrl;

    if (
        (window?.process?.env?.ENVIRONMENT ?? process.env.ENVIRONMENT) ===
            'production' ||
        (window?.process?.env?.ENVIRONMENT ?? process.env.ENVIRONMENT) ===
            'local'
    ) {
        baseUrl =
            country === 'CA'
                ? window?.process?.env?.WEBSDK_BASE_URL_CA ??
                  process.env.WEBSDK_BASE_URL_CA
                : window?.process?.env?.WEBSDK_BASE_URL ??
                  process.env.WEBSDK_BASE_URL;
    } else {
        baseUrl = replaceCurrentPrefixWith('mobile-api');
    }

    return baseUrl ? String(baseUrl).replace(/\/+$/, '') : baseUrl;
};

export const safeRedirect = targetUrl => {
    try {
        // eslint-disable-next-line node/prefer-global/url
        const parsedUrl = new URL(targetUrl, window.location.origin);

        if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
            // eslint-disable-next-line no-console
            console.error('Blocked unsafe redirect URL:', targetUrl);

            return;
        }
        window.location.href = encodeURI(parsedUrl.href);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Invalid redirect URL:', targetUrl, e);
    }
};

export const redirectToMedoptLoginPage = () => {
    if (
        (window?.process?.env?.ENVIRONMENT ?? process.env.ENVIRONMENT) ===
        'local'
    ) {
        safeRedirect(
            `${
                window?.process?.env?.LOGIN_REDIRECT ??
                process.env.LOGIN_REDIRECT
            }/${MEDOPT_SIGNUP}`
        );
    } else {
        safeRedirect(`${replaceCurrentPrefixWith('member')}/${MEDOPT_SIGNUP}`);
    }
};

// Strip characters that should never appear in a legitimate URL pathname or
// query string. This neutralizes any attempt to smuggle control characters or
// HTML/JS metacharacters from `window.location` into a redirect target and
// breaks the Client DOM XSS taint flow flagged by static analysis.
export const sanitizeUrlComponent = value =>
    String(value || '').replace(/[\r\n\t<>"'`\\]/g, '');

/**
 * Joins a member/login base URL with logout or the current sanitized path.
 * Path is expected to include a leading slash when present (pathname + search).
 */
function buildAuthRedirectTarget(baseUrl, isLogout, path) {
    const normalizedBase = (baseUrl ?? '').replace(/\/$/, '');

    if (isLogout) {
        return `${normalizedBase}/logout`;
    }

    return path.startsWith('/')
        ? `${normalizedBase}${path}`
        : `${normalizedBase}/${path}`;
}

export const redirectToLoginPage = (isLogout, memberHost, options = {}) => {
    const nodesAfterPrefix = getNodesAfterPrefix();
    const isTeladocHost = nodesAfterPrefix[0] === DEFAULT_BRAND;
    const isSMBHYHost = nodesAfterPrefix[0] === SMB_HY_BRAND;
    const isPreprodHost = nodesAfterPrefix.includes(PREPROD_NODE_NAME);
    const isUnmaskedHost = nodesAfterPrefix.includes(PM_UNMASKED_NAME);
    const reason = options?.reason ? sanitizeUrlComponent(options.reason) : '';
    const params = sanitizeUrlComponent(window.location.search);
    let path = sanitizeUrlComponent(window.location.pathname);

    if (params) {
        path = path.concat(params);
    }

    const resolveBaseUrl = () => {
        if (process.env.ENVIRONMENT === 'local') {
            return process.env.LOGIN_REDIRECT;
        }

        if (memberHost && OneAppUserAPI.isPLIBrand()) {
            return memberHost;
        }

        if (
            !isSMBHYHost &&
            !isTeladocHost &&
            !isPreprodHost &&
            !isUnmaskedHost
        ) {
            // This is for PLI PROD member domain where prefix is not applied
            return pliProdMemberHost();
        }

        return replaceCurrentPrefixWith('member');
    };

    const baseUrl = resolveBaseUrl();

    if (path?.includes(MEDOPT_SIGNUP)) {
        redirectToMedoptLoginPage();
    } else {
        const destination = buildAuthRedirectTarget(baseUrl, isLogout, path);
        let redirectTarget = isLogout
            ? `${destination}${buildUtmQuery()}`
            : destination;

        if (process.env.ENVIRONMENT === 'local' && reason) {
            redirectTarget = addParamToUrl(
                redirectTarget,
                `reason=${encodeURIComponent(reason)}`
            );
        }

        safeRedirect(redirectTarget);
    }
};

// Redirect URLs used in the Telapp Member App to the OneApp versions
export const redirectMemberAppUrls = pathname => {
    if (!pathname) {
        return;
    }

    let updatedPath;

    if (pathname.includes('my_account/personal_details')) {
        updatedPath = '/account/profile';
    } else if (pathname.includes('my_account/emergency_contact')) {
        updatedPath = '/account/emergency-contacts';
    } else if (pathname.includes('my_family/overview')) {
        updatedPath = '/account/family';
    } else if (pathname.includes('my_account/login_information')) {
        updatedPath = '/account/security-questions';
    }

    if (updatedPath) {
        window.history.pushState({}, '', updatedPath);
    }
};

const UrlUtils = {
    addParamToUrl,
    getUrlParameter,
    getVideoRoomExitPath,
    buildUtmQuery,
    persistUtmCookie,
    getCurrentUrlWithAddedParams,
    prependProtocol,
    getCurrentUrlPrefix,
    getCurrentUrlHostWithoutPrefix,
    getNodesAfterPrefix,
    replaceCurrentPrefixWith,
    getUpdatedWebsdkURL,
    safeRedirect,
    redirectToLoginPage,
    redirectToMedoptLoginPage,
    redirectMemberAppUrls,
};

export default UrlUtils;
